import {
  h,
  clear,
  formatDate,
  formatTime,
  formatDuration,
  localInputToIso,
  isoToLocalInput,
  addDaysLocal,
} from '../ui/dom.js';
import {
  card,
  button,
  field,
  input,
  select,
  openModal,
  toast,
  badge,
  readinessBadge,
  editionStatusBadge,
  metric,
  emptyState,
} from '../ui/components.js';
import { api, ApiError } from '../api.js';
import { can, getPermissions } from '../store.js';
import { confirmEditionRemoval } from './editions.js';
import { openMediaManager } from './media-manager.js';
import type { Page, PageContext } from '../page.js';
import {
  EDITION_STATUS_LABELS,
  READINESS_LABELS,
  type Edition,
  type EditionMinistration,
  type EditionStatus,
  type ReadinessStatus,
  type User,
} from '../types.js';

export async function editionDetailPage(context: PageContext): Promise<Page> {
  const { id } = context.params;
  if (!id) return { title: 'Edição', body: emptyState('Edição não informada.') };

  if (can.isMinistrador()) {
    context.navigate('/minhas-ministracoes');
    return { title: 'Redirecionando…', body: null };
  }

  const edition = await api.get<Edition>(`/api/editions/${id}`);
  const { items: ministrations } = await api.get<{ items: EditionMinistration[] }>(
    `/api/editions/${id}/ministrations`,
  );

  let ministradores: User[] = [];
  try {
    const response = await api.get<{ items: User[] }>('/api/users/ministradores');
    ministradores = response.items;
  } catch {
    ministradores = [];
  }

  const canSchedule = can.createEditions();
  const totalMinutes = ministrations.reduce(
    (sum, item) => sum + item.durationMin + (item.intervalMin ?? 0),
    0,
  );
  const totalSpeaking = ministrations.reduce((sum, item) => sum + item.durationMin, 0);
  const homologadas = ministrations.filter((item) => item.readiness === 'HOMOLOGADO').length;
  const semEscala = ministrations.filter((item) => !item.ministradorId).length;
  const totalDays = (edition.days ?? []).length || 1;

  const timeline = h('div', { class: 'card' });

  const editionDays = [...(edition.days ?? [])].sort((a, b) => a.dayNumber - b.dayNumber);
  const dayOptions = editionDays.map((day) => ({
    value: String(day.dayNumber),
    label: day.label ?? `Dia ${day.dayNumber}`,
  }));

  const renderTimeline = (): void => {
    clear(timeline);
    timeline.append(
      h(
        'div',
        { class: 'card__header' },
        h(
          'div',
          null,
          h('div', { class: 'card__title', text: 'Agenda, linha do tempo e escalas' }),
          h('div', {
            class: 'card__hint',
            text: `${editionDays.length} dia(s) · ${ministrations.length} ministrações · ${formatDuration(totalMinutes)} no total`,
          }),
        ),
        h(
          'div',
          { class: 'row' },
          canSchedule
            ? button('Recalcular agenda', {
                variant: 'ghost',
                size: 'sm',
                onClick: () => void recompute(),
              })
            : null,
          button('Agenda', {
            variant: 'subtle',
            size: 'sm',
            onClick: () => context.navigate(`/edicoes/${id}/agenda`),
          }),
          button('Check-list', {
            variant: 'ghost',
            size: 'sm',
            onClick: () => context.navigate(`/edicoes/${id}/prontidao`),
          }),
          button('Painel de apoio', {
            variant: 'ghost',
            size: 'sm',
            onClick: () => context.navigate(`/edicoes/${id}/apoio`),
          }),
        ),
      ),
    );

    if (ministrations.length === 0) {
      timeline.append(emptyState('Esta edição não possui ministrações.', '▤'));
      return;
    }

    const list = h('div', { class: 'list' });
    const daysToRender =
      editionDays.length > 0
        ? editionDays
        : [
            {
              id: 'fallback',
              editionId: edition.id,
              dayNumber: 1,
              startAt: edition.startDate,
              label: 'Dia 1',
              createdAt: edition.createdAt,
              updatedAt: edition.updatedAt,
            },
          ];

    for (const day of daysToRender) {
      const dayItems = ministrations.filter((item) => item.dayNumber === day.dayNumber);
      const dayMinutes = dayItems.reduce(
        (sum, item) => sum + item.durationMin + (item.intervalMin ?? 0),
        0,
      );

      // Cabeçalho do dia
      list.append(
        h(
          'div',
          {
            class: 'row row--between',
            style: {
              padding: '14px 20px',
              background: 'var(--bg-sunken)',
              borderTop: '1px solid var(--border)',
              borderBottom: '1px solid var(--border)',
            },
          },
          h(
            'div',
            { class: 'row' },
            badge(`Dia ${day.dayNumber}`, 'accent'),
            h('span', { style: { fontWeight: '700' }, text: day.label ?? `Dia ${day.dayNumber}` }),
          ),
          h(
            'div',
            { class: 'row small muted' },
            h('span', {
              class: 'mono',
              text: `${formatDate(day.startAt)} · início ${formatTime(day.startAt)}`,
            }),
            h('span', { text: '·' }),
            h('span', { text: `${dayItems.length} ministração(ões) · ${formatDuration(dayMinutes)}` }),
          ),
        ),
      );

      if (dayItems.length === 0) {
        list.append(
          h(
            'div',
            { style: { padding: '16px 20px' } },
            h('span', { class: 'muted small', text: 'Nenhuma ministração neste dia.' }),
          ),
        );
        continue;
      }

      for (const item of dayItems) {
        const durationInput = input({
          type: 'number',
          value: String(item.durationMin),
          attrs: { min: '5', max: '600', style: 'width:80px' },
          disabled: !canSchedule,
        });

        const intervalInput = input({
          type: 'number',
          value: String(item.intervalMin ?? 0),
          attrs: { min: '0', max: '240', style: 'width:80px' },
          disabled: !canSchedule,
        });

        const ministradorSelect = select(
          [
            { value: '', label: '— Não escalado —' },
            ...ministradores.map((user) => ({ value: user.id, label: user.name })),
          ],
          { value: item.ministradorId ?? '', disabled: !canSchedule },
        );

        const daySelect = select(dayOptions, {
          value: String(item.dayNumber),
          disabled: !canSchedule,
        });

        list.append(
          h(
            'div',
            { class: 'list__item' },
            h('div', { class: 'list__index', text: String(item.position) }),
            h(
              'div',
              { class: 'list__body' },
              h(
                'div',
                { class: 'row row--between' },
                h(
                  'div',
                  null,
                  h('div', { class: 'list__title', text: item.title }),
                  h('div', {
                    class: 'muted small',
                    text: item.theme ?? 'Sem tema definido',
                  }),
                ),
                readinessBadge(item.readiness),
              ),
              h(
                'div',
                { class: 'row', style: { marginTop: '6px' } },
                h('span', {
                  class: 'mono',
                  text: `${formatTime(item.scheduledStart)} — ${formatTime(item.scheduledEnd)}`,
                }),
                h('span', { class: 'faint', text: '·' }),
                h('span', {
                  class: 'muted small',
                  text: `${formatDuration(item.durationMin)}${item.intervalMin > 0 ? ` + ${formatDuration(item.intervalMin)}` : ''}`,
                }),
                h('span', { class: 'faint', text: '·' }),
                h('span', { class: 'muted small', text: `${item.mediaCount} material(is)` }),
                item.ministradorName
                  ? badge(item.ministradorName, 'info')
                  : badge('Sem ministrador', 'danger'),
              ),
              canSchedule
                ? h(
                    'div',
                    { class: 'row', style: { marginTop: '10px', alignItems: 'flex-end' } },
                    h(
                      'div',
                      { class: 'field', style: { maxWidth: '110px' } },
                      h('span', { class: 'field__label', text: 'Dia' }),
                      daySelect,
                    ),
                    h(
                      'div',
                      { class: 'field', style: { maxWidth: '240px' } },
                      h('span', { class: 'field__label', text: 'Ministrador' }),
                      ministradorSelect,
                    ),
                    h(
                      'div',
                      { class: 'field', style: { maxWidth: '110px' } },
                      h('span', { class: 'field__label', text: 'Duração' }),
                      durationInput,
                    ),
                    h(
                      'div',
                      { class: 'field', style: { maxWidth: '110px' } },
                      h('span', { class: 'field__label', text: 'Intervalo' }),
                      intervalInput,
                    ),
                    button('Salvar', {
                      size: 'sm',
                      variant: 'ghost',
                      onClick: () =>
                        void saveRow(
                          item,
                          ministradorSelect.value,
                          Number(durationInput.value),
                          Number(intervalInput.value),
                          Number(daySelect.value),
                        ),
                    }),
                    button('Homologar', {
                      size: 'sm',
                      variant: 'subtle',
                      disabled: item.readiness === 'HOMOLOGADO',
                      onClick: () => void setReadiness(item, 'HOMOLOGADO'),
                    }),
                    button('Reabrir', {
                      size: 'sm',
                      variant: 'ghost',
                      disabled: item.readiness !== 'HOMOLOGADO',
                      onClick: () => void setReadiness(item, 'EM_PREPARACAO'),
                    }),
                  )
                : null,
            ),
            h(
              'div',
              { class: 'stack stack--sm', style: { alignItems: 'flex-end' } },
              button(`Materiais / mídias${item.mediaCount > 0 ? ` (${item.mediaCount})` : ''}`, {
                size: 'sm',
                variant: item.mediaCount > 0 ? 'subtle' : 'ghost',
                onClick: () => openEditionMedia(item, edition),
              }),
              button('Púlpito', {
                size: 'sm',
                variant: 'subtle',
                onClick: () => context.navigate(`/ministracoes/${item.id}/pulpito`),
              }),
            ),
          ),
        );
      }
    }

    timeline.append(list);
  };

  const reload = (): void => window.location.reload();

  const recompute = async (): Promise<void> => {
    try {
      await api.post(`/api/editions/${id}/schedule/recompute`);
      toast.success('Grade recalculada');
      reload();
    } catch (error) {
      toast.error('Falha ao recalcular', error instanceof ApiError ? error.message : undefined);
    }
  };

  const saveRow = async (
    item: EditionMinistration,
    ministradorId: string,
    durationMin: number,
    intervalMin: number,
    dayNumber: number,
  ): Promise<void> => {
    try {
      if (ministradorId !== (item.ministradorId ?? '')) {
        await api.post(`/api/ministrations/${item.id}/assign`, {
          ministradorId: ministradorId || null,
        });
      }

      const patch: Record<string, number> = {};
      if (durationMin !== item.durationMin) patch.durationMin = durationMin;
      if (intervalMin !== (item.intervalMin ?? 0)) patch.intervalMin = intervalMin;
      if (dayNumber !== item.dayNumber) patch.dayNumber = dayNumber;

      if (Object.keys(patch).length > 0) {
        await api.patch(`/api/ministrations/${item.id}`, patch);
      }

      toast.success('Agenda atualizada');
      reload();
    } catch (error) {
      toast.error('Falha ao salvar', error instanceof ApiError ? error.message : undefined);
    }
  };

  const setReadiness = async (item: EditionMinistration, readiness: ReadinessStatus): Promise<void> => {
    try {
      await api.post(`/api/ministrations/${item.id}/readiness`, { readiness });
      toast.success('Status atualizado', `${item.title} · ${READINESS_LABELS[readiness]}`);
      reload();
    } catch (error) {
      toast.error('Falha ao atualizar', error instanceof ApiError ? error.message : undefined);
    }
  };

  renderTimeline();

  const actions = [
    button('Voltar', { variant: 'ghost', size: 'sm', onClick: () => context.navigate('/edicoes') }),
  ];

  if (can.manageEditions()) {
    actions.push(
      button('Editar edição', {
        variant: 'ghost',
        size: 'sm',
        onClick: () => openEditionEditModal(edition, reload),
      }),
      button('Excluir', {
        variant: 'danger',
        size: 'sm',
        onClick: () =>
          confirmEditionRemoval(edition, async () => {
            await api.delete(`/api/editions/${id}`);
            toast.success('Edição excluída');
            context.navigate('/edicoes');
          }),
      }),
    );
  }

  return {
    title: edition.name,
    subtitle: `${edition.location ?? 'Local não informado'} · ${formatDate(edition.startDate)}`,
    actions,
    body: [
      h(
        'div',
        { class: 'grid grid--4' },
        metric('Dias de encontro', totalDays, 'accent'),
        metric('Ministrações', ministrations.length, 'info'),
        metric('Tempo total', formatDuration(totalMinutes), 'success'),
        metric('Fala / intervalos', `${formatDuration(totalSpeaking)} / ${formatDuration(totalMinutes - totalSpeaking)}`),
      ),
      h(
        'div',
        { class: 'row' },
        editionStatusBadge(edition.status),
        badge(`${totalDays} dia(s)`, 'accent'),
        badge(`Início ${formatDate(edition.startDate)}`, 'neutral'),
        badge(`${homologadas}/${ministrations.length} homologadas`, homologadas === ministrations.length ? 'success' : 'warning'),
        semEscala > 0 ? badge(`${semEscala} sem ministrador`, 'danger') : null,
        badge(`Término ${formatTime(ministrations[ministrations.length - 1]?.scheduledEnd ?? null)}`, 'neutral'),
      ),
      timeline,
    ],
  };
}

/* ---------- Edicao dos dados da edicao ------------------------------------ */

function openEditionEditModal(edition: Edition, onSaved: () => void): void {
  const nameInput = input({ value: edition.name });
  const locationInput = input({ value: edition.location ?? '' });
  const statusSelect = select(
    (Object.keys(EDITION_STATUS_LABELS) as EditionStatus[]).map((status) => ({
      value: status,
      label: EDITION_STATUS_LABELS[status],
    })),
    { value: edition.status },
  );
  const errorSlot = h('div', { class: 'field__error' });

  /* ---------- Dias do encontro ---------------------------------------- */

  interface DayDraft {
    id?: string;
    start: string;
    label: string;
  }

  const existingDays = [...(edition.days ?? [])].sort((a, b) => a.dayNumber - b.dayNumber);
  const days: DayDraft[] =
    existingDays.length > 0
      ? existingDays.map((day, index) => ({
          id: day.id,
          start: isoToLocalInput(day.startAt),
          label: day.label ?? `Dia ${index + 1}`,
        }))
      : [{ start: isoToLocalInput(edition.startDate), label: 'Dia 1' }];

  const daysContainer = h('div', { class: 'stack' });
  const daysSummary = h('div', { class: 'muted small' });

  const renderDays = (): void => {
    clear(daysContainer);
    daysSummary.textContent =
      days.length === 1
        ? 'Encontro de 1 dia.'
        : `Encontro de ${days.length} dias — a agenda é separada por dia.`;

    days.forEach((day, index) => {
      const dateField = input({
        type: 'datetime-local',
        value: day.start,
        attrs: { required: 'true' },
        oninput: (event: Event) => {
          day.start = (event.target as HTMLInputElement).value;
        },
      });

      const labelField = input({
        value: day.label,
        placeholder: `Dia ${index + 1}`,
        oninput: (event: Event) => {
          day.label = (event.target as HTMLInputElement).value;
        },
      });

      daysContainer.append(
        h(
          'div',
          { class: 'card', style: { padding: '12px' } },
          h(
            'div',
            { class: 'row row--between', style: { marginBottom: '8px' } },
            h('div', { class: 'badge badge--accent', text: `Dia ${index + 1}` }),
            days.length > 1
              ? button('Remover', {
                  size: 'sm',
                  variant: 'ghost',
                  onClick: () => {
                    days.splice(index, 1);
                    renderDays();
                  },
                })
              : null,
          ),
          h(
            'div',
            { class: 'form-grid' },
            field({ label: 'Início das atividades', control: dateField, required: true }),
            field({ label: 'Rótulo', control: labelField, hint: 'Ex.: Sexta à noite, Sábado manhã.' }),
          ),
        ),
      );
    });
  };

  const addDay = (): void => {
    const firstIso = days[0]?.start ? localInputToIso(days[0].start) : null;
    const suggested = firstIso ? isoToLocalInput(addDaysLocal(firstIso, days.length)) : '';
    days.push({ start: suggested, label: `Dia ${days.length + 1}` });
    renderDays();
  };

  renderDays();

  const submit = async (): Promise<void> => {
    clear(errorSlot);

    if (days.some((day) => !day.start)) {
      errorSlot.textContent = 'Informe a data e hora de início de todos os dias.';
      return;
    }

    try {
      await api.patch(`/api/editions/${edition.id}`, {
        name: nameInput.value.trim(),
        location: locationInput.value.trim() || null,
        status: statusSelect.value,
        startDate: localInputToIso(days[0]?.start ?? '') ?? undefined,
        days: days.map((day, index) => ({
          ...(day.id ? { id: day.id } : {}),
          dayNumber: index + 1,
          startAt: localInputToIso(day.start) ?? undefined,
          label: day.label.trim() || `Dia ${index + 1}`,
        })),
      });
      modal?.close();
      toast.success('Edição atualizada', 'A agenda foi recalculada para todos os dias.');
      onSaved();
    } catch (error) {
      errorSlot.textContent = error instanceof ApiError ? error.message : 'Falha ao salvar.';
    }
  };

  let modal: { close: () => void } | null = null;
  modal = openModal({
    title: 'Editar edição',
    wide: true,
    body: [
      h(
        'div',
        { class: 'form-grid' },
        field({ label: 'Nome', control: nameInput, required: true }),
        field({ label: 'Local', control: locationInput }),
        field({
          label: 'Status',
          control: statusSelect,
          hint: 'Alterar dias ou horários recalcula a agenda automaticamente.',
        }),
      ),
      h('hr', { class: 'separator' }),
      h(
        'div',
        { class: 'row row--between' },
        h(
          'div',
          null,
          h('div', { class: 'card__title', text: 'Agenda — dias do encontro' }),
          daysSummary,
        ),
        button('+ Adicionar dia', { size: 'sm', variant: 'ghost', onClick: addDay }),
      ),
      daysContainer,
      errorSlot,
    ],
    footer: [
      button('Cancelar', { variant: 'ghost', onClick: () => modal?.close() }),
      button('Salvar', { onClick: () => void submit() }),
    ],
  });
}

function toLocalInput(iso: string): string {
  return isoToLocalInput(iso);
}

/* ---------- Repositório de mídias da ministração -------------------------- */

/**
 * Abre o gerenciador de materiais de uma ministração da edição.
 * A escrita segue a regra do servidor: `manage` sempre pode; `active`
 * (Coordenador Geral) apenas em edições com status ATIVA; os demais perfis
 * têm acesso somente leitura.
 */
function openEditionMedia(item: EditionMinistration, edition: Edition): void {
  const repository = getPermissions()?.repository ?? 'none';
  const canWrite =
    repository === 'manage' || (repository === 'active' && edition.status === 'ATIVA');

  openMediaManager({
    title: `Materiais · ${item.title}`,
    subtitle:
      'Envie slides, PDFs, imagens, áudio e vídeo — ou vincule links do YouTube e Spotify. A equipe de apoio baixa os arquivos homologados por aqui.',
    scope: 'EDITION_MINISTRATION',
    ownerId: item.id,
    basePath: `/api/ministrations/${item.id}/media`,
    canWrite,
    canIncorporate: can.incorporateOfficial(),
    readOnlyHint:
      repository === 'active'
        ? 'A Coordenação Geral só pode alterar materiais de edições ativas.'
        : 'Seu perfil tem acesso somente leitura ao repositório multimídia.',
  });
}
